import * as React from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Plus, Search, Trash2, X } from 'lucide-react'

import {
  useCreateRegistrar, useDeleteRegistrar,
  useRegistrars, useRemoveRegistrarPrice,
  useSetRegistrarPrice, useUpdateRegistrar,
  type Registrar, type RegistrarInput, type RegistrarPrice,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Label } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DrawerContent,
} from '@/components/ui/dialog'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { FilterToggle } from '@/components/ui/filter-select'
import { useToast } from '@/components/ui/toast'
import { formatNumber } from '@/lib/utils'

/**
 * Prices are stored in minor units so no arithmetic ever touches a float. The
 * UI works in major units because that is what a price list is written in.
 */
function toMajor(minor?: number): string {
  return minor == null ? '' : (minor / 100).toFixed(2)
}

function toMinor(major: string): number | undefined {
  const n = Number(major)
  return major.trim() === '' || Number.isNaN(n) ? undefined : Math.round(n * 100)
}

/**
 * The cheapest registration on offer, with how many suffixes are priced.
 *
 * A single column cannot show a whole price list, and showing one arbitrary
 * TLD's price would read as "this registrar costs X" — which is the confusion
 * the per-TLD table exists to end. The lowest price plus a count says what the
 * column can honestly say; the detail is one click away.
 */
function priceSummary(r: Registrar): { label: string; hint: string } {
  const priced = (r.prices ?? []).filter((p) => p.register_price != null)
  if (priced.length === 0) return { label: '—', hint: '未设置价格' }
  const cheapest = priced.reduce((a, b) => (a.register_price! <= b.register_price! ? a : b))
  return {
    label: `${cheapest.currency} ${toMajor(cheapest.register_price)} 起`,
    hint: priced
      .map((p) => `.${p.tld}  ${p.currency} ${toMajor(p.register_price)}`)
      .join('\n'),
  }
}

export default function RegistrarsPage() {
  const { user } = useAuth()
  const toast = useToast()
  const isAdmin = user?.role === 'admin'

  const [query, setQuery] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [linkedOnly, setLinkedOnly] = React.useState(true)
  const [apiOnly, setApiOnly] = React.useState(false)
  const [editingID, setEditingID] = React.useState<string | null>(null)
  const [creating, setCreating] = React.useState(false)
  const [deleting, setDeleting] = React.useState<Registrar | null>(null)

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  const { data, isLoading, error, refetch } = useRegistrars({
    q: debounced || undefined,
    linked: linkedOnly || undefined,
    api_supported: apiOnly || undefined,
  })
  const removeRegistrar = useDeleteRegistrar()

  const items = data?.registrars ?? []
  // Read the row being edited out of the live list rather than holding the
  // object captured when the dialog opened: adding a price or an alias
  // invalidates the query, and a captured copy would keep showing the state
  // from before the edit — the row saves but the table never shows it.
  const editing = editingID ? items.find((r) => r.id === editingID) ?? null : null

  return (
    <div>
      <PageHeader
        title="注册商"
        description={
          <span>
            以 IANA 编号为准的注册商目录。「显示名称」决定域名库里怎么显示，
            「匹配写法」决定注册局返回什么算这一家——两者互不相干。
          </span>
        }
        actions={isAdmin && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> 新增注册商
          </Button>
        )}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索名称、显示名称、匹配写法或 IANA 编号…"
            className="h-9 pl-8"
            aria-label="搜索注册商"
          />
        </div>
        <FilterToggle label="仅有域名的" active={linkedOnly} onToggle={() => setLinkedOnly((v) => !v)} />
        <FilterToggle label="支持 API" active={apiOnly} onToggle={() => setApiOnly((v) => !v)} />
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="没有符合条件的注册商"
          description="调整搜索或筛选条件；目录会随着域名解析结果自动增长。"
          icon={Search}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <div className="scroll-x">
            <table className="w-full min-w-[1100px] text-sm">
              <caption className="sr-only">注册商目录，共 {items.length} 家</caption>
              <thead className="border-b bg-muted/40">
                <tr>
                  {['注册商', 'IANA', '域名数', 'API', '可抢注', '抢注成功率', '注册价', '操作'].map((h) => (
                    <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.short_name || r.name}</div>
                      {r.short_name && (
                        <div className="max-w-[280px] truncate text-xs text-muted-foreground">{r.name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{r.iana_id || '—'}</td>
                    <td className="px-3 py-2">
                      {r.domain_count > 0 ? (
                        <Link
                          to={`/domains?registrar_id=${r.id}`}
                          className="text-sm tabular-nums text-primary hover:underline"
                        >
                          {formatNumber(r.domain_count)}
                        </Link>
                      ) : (
                        <span className="text-sm text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2"><YesNo value={r.api_supported} /></td>
                    <td className="px-3 py-2"><YesNo value={r.drop_catch_ready} /></td>
                    <td className="px-3 py-2">
                      {/* Never a bare percentage: one success from one attempt
                          and 400 from 400 are both "100%", and only the count
                          says which. */}
                      {r.catch_attempts === 0 ? (
                        <span className="text-xs text-muted-foreground">未尝试</span>
                      ) : (
                        <span className="text-xs tabular-nums">
                          {Math.round((r.catch_rate ?? 0) * 100)}%
                          <span className="ml-1 text-muted-foreground">
                            ({r.catch_successes}/{r.catch_attempts})
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {(() => {
                        const { label, hint } = priceSummary(r)
                        return (
                          <Tooltip content={hint}>
                            <span className="text-xs tabular-nums">
                              {label}
                              {(r.prices?.length ?? 0) > 1 && (
                                <span className="ml-1 text-muted-foreground">
                                  ({r.prices!.length} 个后缀)
                                </span>
                              )}
                            </span>
                          </Tooltip>
                        )
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      {isAdmin ? (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon-sm" onClick={() => setEditingID(r.id)} aria-label={`编辑 ${r.name}`}>
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                          <Button
                            variant="ghost" size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleting(r)}
                            aria-label={`删除 ${r.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(editing || creating) && (
        <RegistrarDialog
          registrar={editing}
          open
          onOpenChange={(open) => { if (!open) { setEditingID(null); setCreating(false) } }}
        />
      )}

      <Dialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) setDeleting(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>删除注册商</DialogTitle>
            <DialogDescription>
              将删除「{deleting?.name}」及其全部别名。
              {(deleting?.domain_count ?? 0) > 0 && (
                <> 该注册商下的 {formatNumber(deleting?.domain_count ?? 0)} 个域名会保留，
                但不再关联到目录（名称仍在域名记录上）。</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>取消</Button>
            <Button
              variant="destructive"
              disabled={removeRegistrar.isPending}
              onClick={async () => {
                if (!deleting) return
                try {
                  await removeRegistrar.mutateAsync(deleting.id)
                  toast.success('已删除', deleting.name)
                  setDeleting(null)
                } catch (err) {
                  toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
                }
              }}
            >
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function YesNo({ value }: { value: boolean }) {
  return value ? (
    <Badge variant="default" className="text-[10px]">是</Badge>
  ) : (
    <span className="text-xs text-muted-foreground">否</span>
  )
}

function RegistrarDialog({
  registrar, open, onOpenChange,
}: { registrar: Registrar | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast()
  const create = useCreateRegistrar()
  const update = useUpdateRegistrar()

  const [form, setForm] = React.useState<RegistrarInput>(() => ({
    name: registrar?.name ?? '',
    short_name: registrar?.short_name ?? '',
    iana_id: registrar?.iana_id ?? '',
    website: registrar?.website ?? '',
    api_supported: registrar?.api_supported ?? false,
    drop_catch_ready: registrar?.drop_catch_ready ?? false,
    api_notes: registrar?.api_notes ?? '',
    currency: registrar?.currency ?? 'CNY',
    price_note: registrar?.price_note ?? '',
    notes: registrar?.notes ?? '',
  }))

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const body: RegistrarInput = { ...form }
    try {
      if (registrar) {
        await update.mutateAsync({ id: registrar.id, body })
        toast.success('已保存', registrar.name)
      } else {
        const created = await create.mutateAsync(body)
        toast.success('已新增', created.name)
      }
      onOpenChange(false)
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A drawer rather than a centred modal: this panel carries two editable
          tables (prices, matching spellings) that grow as you use it, and a
          centred box either scrolls its own header out of view or jumps in size
          on every added row. */}
      <DrawerContent className="sm:max-w-xl" aria-describedby={undefined}>
        <form onSubmit={submit} className="flex h-full flex-col">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>{registrar ? '编辑注册商' : '新增注册商'}</DialogTitle>
            <DialogDescription>
              IANA 编号是注册商的真实身份，改名不会打散它名下的域名。
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">

          <div className="grid grid-cols-2 gap-3">
            <Field label="名称" required>
              <Input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required />
            </Field>
            <Field label="显示名称" hint="域名库、筛选器中改用它显示">
              <Input value={form.short_name ?? ''} onChange={(e) => set('short_name', e.target.value)} placeholder="如 万网" />
            </Field>
            <Field label="IANA 编号">
              <Input value={form.iana_id ?? ''} onChange={(e) => set('iana_id', e.target.value)} placeholder="如 1599" />
            </Field>
            <Field label="官网">
              <Input value={form.website ?? ''} onChange={(e) => set('website', e.target.value)} placeholder="https://" />
            </Field>
          </div>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-medium text-muted-foreground">抢注能力</legend>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={Boolean(form.api_supported)} onCheckedChange={(v) => set('api_supported', Boolean(v))} />
              提供注册 API
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={Boolean(form.drop_catch_ready)} onCheckedChange={(v) => set('drop_catch_ready', Boolean(v))} />
              可用于抢注
            </label>
            <Input
              value={form.api_notes ?? ''}
              onChange={(e) => set('api_notes', e.target.value)}
              placeholder="接口限制、并发上限等备注"
            />
          </fieldset>

          {registrar ? (
            <PriceEditor registrar={registrar} />
          ) : (
            <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
              各后缀的价格在保存后添加——一个注册商的 .com 与 .cn 是两个不相干的价格，
              所以它们是独立的多条记录，而不是这里的一个数字。
            </div>
          )}


          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>保存</Button>
          </DialogFooter>
        </form>
      </DrawerContent>
    </Dialog>
  )
}

function Field({
  label, hint, required, children,
}: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}{required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && <p className="text-[0.7rem] text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * The per-TLD price list for one registrar.
 *
 * A registrar's .com and .cn prices are unrelated numbers, so each suffix is
 * its own row rather than a field on the registrar. Renewal is shown beside
 * registration because a cheap first year followed by an expensive renewal is
 * the normal shape of this market, and a list showing only registration would
 * rank exactly the wrong registrar first.
 */
function PriceEditor({ registrar }: { registrar: Registrar }) {
  const toast = useToast()
  const setPrice = useSetRegistrarPrice()
  const removePrice = useRemoveRegistrarPrice()

  const [draft, setDraft] = React.useState<{
    tld: string; currency: string; register: string; renew: string; note: string
  }>({ tld: '', currency: registrar.currency || 'CNY', register: '', renew: '', note: '' })

  const prices = registrar.prices ?? []
  const canAdd = draft.tld.trim() !== '' && !setPrice.isPending

  async function save(price: RegistrarPrice) {
    try {
      await setPrice.mutateAsync({ id: registrar.id, price })
      toast.success('已保存价格', `.${price.tld}`)
      return true
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
      return false
    }
  }

  async function addDraft() {
    const ok = await save({
      tld: draft.tld,
      currency: draft.currency,
      register_price: toMinor(draft.register),
      renew_price: toMinor(draft.renew),
      note: draft.note,
    })
    if (ok) setDraft((d) => ({ ...d, tld: '', register: '', renew: '', note: '' }))
  }

  return (
    <fieldset className="space-y-2 rounded-md border p-3">
      <legend className="px-1 text-xs font-medium text-muted-foreground">分后缀费率</legend>
      {/* The add row lives inside the table so it sits under the same column
          headers. As a detached strip of inputs it had no labels at all, and
          the 后缀 placeholder "com" read as a value already filled in. */}
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              {['后缀', '币种', '注册价', '续费价', '备注', ''].map((h) => (
                <th key={h} className="px-2 py-1.5 text-left font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => (
              <tr key={p.tld} className="border-t">
                <td className="px-2 py-1.5 font-medium">.{p.tld}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{p.currency}</td>
                <td className="px-2 py-1.5 tabular-nums">{toMajor(p.register_price) || '—'}</td>
                <td className="px-2 py-1.5 tabular-nums">{toMajor(p.renew_price) || '—'}</td>
                <td className="max-w-[120px] truncate px-2 py-1.5 text-muted-foreground">{p.note || '—'}</td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    aria-label={`删除 .${p.tld} 的价格`}
                    onClick={() => void removePrice.mutateAsync({ id: registrar.id, tld: p.tld })}
                    className="rounded text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
            {prices.length === 0 && (
              <tr className="border-t">
                <td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">
                  还没有价格，在下面一行填写后添加
                </td>
              </tr>
            )}

            <tr className="border-t bg-muted/20">
              <td className="px-1.5 py-1.5">
                <Input
                  value={draft.tld}
                  onChange={(e) => setDraft((d) => ({ ...d, tld: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canAdd) void addDraft() } }}
                  placeholder="如 com"
                  aria-label="后缀"
                  className="h-7 w-16 px-1.5 text-xs"
                />
              </td>
              <td className="px-1.5 py-1.5">
                <Input
                  value={draft.currency}
                  onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value.toUpperCase() }))}
                  aria-label="币种"
                  className="h-7 w-16 px-1.5 text-xs"
                />
              </td>
              <td className="px-1.5 py-1.5">
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.register}
                  onChange={(e) => setDraft((d) => ({ ...d, register: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canAdd) void addDraft() } }}
                  aria-label="注册价"
                  className="h-7 w-20 px-1.5 text-xs"
                />
              </td>
              <td className="px-1.5 py-1.5">
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.renew}
                  onChange={(e) => setDraft((d) => ({ ...d, renew: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canAdd) void addDraft() } }}
                  aria-label="续费价"
                  className="h-7 w-20 px-1.5 text-xs"
                />
              </td>
              <td className="px-1.5 py-1.5">
                <Input
                  value={draft.note}
                  onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canAdd) void addDraft() } }}
                  placeholder="促销等"
                  aria-label="备注"
                  className="h-7 w-full min-w-[5rem] px-1.5 text-xs"
                />
              </td>
              <td className="px-1.5 py-1.5 text-right">
                <Button
                  type="button" variant="outline" size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={!canAdd}
                  onClick={() => void addDraft()}
                >
                  <Plus className="h-3 w-3" aria-hidden /> 添加
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </fieldset>
  )
}
